import {
	CurrencyType,
	DocumentRelationRes,
	SecurityIssuedIn,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number.ts';
import { formatValueWithUnitDenominator } from '@dealstack/shared/lib/format/number';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';

const makeTransactionValue = ({
	securityIssuedIn,
	units,
	cost,
	securityCurrency,
	securityUnitDenominator,
}: {
	securityIssuedIn: SecurityIssuedIn;
	units?: number | string;
	cost?: number | string;
	securityCurrency?: CurrencyType;
	securityUnitDenominator: number;
}) => {
	const issuedInCurrency = securityIssuedIn === SecurityIssuedIn.CURRENCY;

	// Convert string values to numbers (API is currently returning strings)
	const numericCost = typeof cost === 'string' ? Number(cost) : cost;
	const numericUnits = typeof units === 'string' ? Number(units) : units;

	if (issuedInCurrency) {
		return formatNumberToUI(numericCost || 0, securityCurrency);
	}

	return `${formatValueWithUnitDenominator(numericUnits ?? 0, securityUnitDenominator)} units`;
};

const capitalizeTransactionType = (transactionType: string) => {
	const words = transactionType.split('_');
	return words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};

// Need to do this hack so we can use optional properties for fields in DocumentRelationRes
type OptionalDocumentRelationRes = Omit<
	DocumentRelationRes,
	| 'transactionType'
	| 'securityName'
	| 'transactionDate'
	| 'buyerName'
	| 'sellerName'
	| 'md5'
	| 'documentId'
	| 'documentName'
	| 'fileId'
	| 'personId'
	| 'linkedTransactionId'
	| 'transactionId'
	| 'issuerId'
	| 'issuerType'
	| 'issuerName'
	| 'buyerId'
	| 'buyerType'
	| 'sellerId'
	| 'sellerType'
	| 'securityType'
	| 'securityIssuedIn'
	| 'securityCurrency'
> &
	Partial<
		Pick<
			DocumentRelationRes,
			'transactionType' | 'securityName' | 'transactionDate' | 'buyerName' | 'sellerName'
		>
	>;

export const constructTransactionSentence = ({
	securityName = 'Unknown Security',
	transactionDate,
	transactionType,
	securityIssuedIn: nullableSecurityIssuedIn,
	securityCurrency: nullableSecurityCurrency,
	buyerName = 'Unknown Buyer',
	sellerName = 'Unknown Seller',
	units,
	cost,
	preEventSecurityName = 'Unknown Security',
	preEventHoldingUnits,
	postEventHoldingUnits,
	securityUnitDenominator,
}: OptionalDocumentRelationRes & {
	securityIssuedIn?: SecurityIssuedIn | null;
	securityCurrency?: CurrencyType | null;
	preEventSecurityName?: string;
	preEventHoldingUnits?: number;
	postEventHoldingUnits?: number;
}): string => {
	const securityIssuedIn = nullableSecurityIssuedIn ?? SecurityIssuedIn.CURRENCY;
	const securityCurrency = nullableSecurityCurrency ?? undefined;

	if (!transactionType) {
		return `Unknown Transaction type: ${transactionType}`;
	}

	const value = makeTransactionValue({
		securityIssuedIn,
		securityCurrency,
		securityUnitDenominator,
		units,
		cost,
	});

	// Transaction/holding-event dates are date-only — render the stored UTC
	// calendar date so the sentence reads identically in every viewer timezone.
	const fromattedTransactionDate = formatBEDateToUiWithFallback(
		transactionDate,
		DATE_FORMATS.UI_SHORT,
		'Unknown Date'
	);

	const hasPreOrPostEventHoldingUnits =
		typeof preEventHoldingUnits === 'number' || typeof postEventHoldingUnits === 'number';
	const prePostHoldingUnitsDifference = hasPreOrPostEventHoldingUnits
		? Math.abs((postEventHoldingUnits || 0) - (preEventHoldingUnits || 0))
		: 0;

	const transactionSentenceMap: Record<TransactionType, () => string> = {
		[TransactionType.CANCELLATION]: () =>
			`${capitalizeTransactionType(transactionType)} of ${value} ${securityName} held by ${sellerName} on ${fromattedTransactionDate}`,

		[TransactionType.ISSUE]: () =>
			`${capitalizeTransactionType(transactionType)} of ${value} ${securityName} to ${buyerName} on ${fromattedTransactionDate}`,

		[TransactionType.REDEMPTION]: () =>
			`${capitalizeTransactionType(transactionType)} of ${value} ${securityName} from ${sellerName} on ${fromattedTransactionDate}`,

		[TransactionType.TRANSFER]: () =>
			`${capitalizeTransactionType(transactionType)} of ${value} ${securityName} from ${sellerName} to ${buyerName} on ${fromattedTransactionDate}`,

		[TransactionType.ISSUE_SPLIT]: () => {
			if (hasPreOrPostEventHoldingUnits) {
				return `${preEventHoldingUnits} units were SPLIT into ${postEventHoldingUnits} on ${fromattedTransactionDate}. ${prePostHoldingUnitsDifference} new units were created.`;
			}
			return `${transactionType} of ${value} ${securityName} from ${sellerName} to ${buyerName} on ${fromattedTransactionDate}`;
		},

		[TransactionType.REDEMPTION_REVERSE_SPLIT]: () => {
			if (hasPreOrPostEventHoldingUnits) {
				return `${preEventHoldingUnits} units were REVERSE_SPLIT into ${postEventHoldingUnits} on ${fromattedTransactionDate}. ${prePostHoldingUnitsDifference} units were cancelled.`;
			}
			return `${transactionType} of ${value} ${securityName} from ${sellerName} on ${fromattedTransactionDate}`;
		},

		[TransactionType.TRANSFER_CONVERSION]: () => {
			if (hasPreOrPostEventHoldingUnits) {
				return `${preEventHoldingUnits} units of ${preEventSecurityName} were CONVERTED into ${postEventHoldingUnits} units of ${securityName} on ${fromattedTransactionDate}.`;
			}
			return `${transactionType} of ${value} ${securityName} from ${sellerName} on ${fromattedTransactionDate}`;
		},

		[TransactionType.SHAREHOLDER_CONTRIBUTION]: () =>
			`${capitalizeTransactionType(transactionType)} (${securityName}) of ${formatNumberToUI(cost ?? 0, securityCurrency)} by ${buyerName} on ${fromattedTransactionDate}`,

		[TransactionType.REDUCTION_IN_SHARE_CAPITAL]: () =>
			`${capitalizeTransactionType(transactionType)} (${securityName}) of ${formatNumberToUI(cost ?? 0, securityCurrency)} by ${sellerName} on ${fromattedTransactionDate}`,

		// Matches `getTransactionTypeText` and the document: this type is a CONVERSION.
		[TransactionType.CONVERSION_SHARE_CAPITAL]: () =>
			`Conversion of Share Capital of ${formatNumberToUI(cost ?? 0, securityCurrency)} from ${preEventSecurityName} to ${securityName} on ${fromattedTransactionDate}`,

		[TransactionType.DIVIDEND]: () =>
			`${capitalizeTransactionType(transactionType)} (${securityName}) of ${formatNumberToUI(cost ?? 0, securityCurrency)} to ${buyerName} on ${fromattedTransactionDate}`,

		// See INTERNAL_ONLY_TRANSACTION_TYPES in transactions.zod.ts.
		[TransactionType.VESTING_EVENT]: () => '',
	};

	return transactionSentenceMap[transactionType]();
};
